import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navigation from '@/components/feature/Navigation';
import SEOHead from '@/components/feature/SEOHead';
import PublicationDetail from './components/PublicationDetail';
import { publications } from '@/mocks/publications';

function buildPublicationSchema(pub: typeof publications[number]) {
  const baseUrl = `https://oludamolaadebowale.com/publications/${pub.id}`;
  const isBook = pub.type === 'Book';

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@id': baseUrl,
    name: pub.title,
    description: pub.abstract.slice(0, 200),
    author: { '@id': 'https://oludamolaadebowale.com/#person' },
    publisher: { '@type': 'Organization', name: pub.publisher },
    datePublished: pub.year,
    inLanguage: 'en',
    image: pub.coverImage ?? undefined,
    url: baseUrl,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://oludamolaadebowale.com/' },
        { '@type': 'ListItem', position: 2, name: 'Publications', item: 'https://oludamolaadebowale.com/publications' },
        { '@type': 'ListItem', position: 3, name: pub.title, item: baseUrl },
      ],
    },
  };

  if (isBook) {
    schema['@type'] = 'Book';
    if (pub.isbn) schema['isbn'] = pub.isbn;
    if (pub.pages) schema['numberOfPages'] = parseInt(pub.pages, 10) || undefined;
  } else if (pub.type === 'Journal Article') {
    schema['@type'] = 'ScholarlyArticle';
  } else {
    schema['@type'] = 'CreativeWork';
    schema['genre'] = pub.type;
  }

  return schema;
}

export default function PublicationDetailPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);
  const publication = publications.find((p) => String(p.id) === id);

  if (!publication) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <Navigation />
        <div className="text-center pt-32 pb-20">
          <p className="font-serif text-2xl text-gray-500 mb-4">Publication not found</p>
          <Link
            to="/publications"
            className="font-sans text-sm text-[#b89a6a] hover:underline cursor-pointer"
          >
            Back to all publications
          </Link>
        </div>
      </div>
    );
  }

  const related = publications
    .filter(
      (p) =>
        p.id !== publication.id &&
        (p.type === publication.type || p.topic.some((t) => publication.topic.includes(t)))
    )
    .slice(0, 3);

  const pubSchema = buildPublicationSchema(publication);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${publication.title} | Oludamola Adebowale`}
        description={publication.abstract.slice(0, 155) + (publication.abstract.length > 155 ? '…' : '')}
        keywords={`${publication.title}, Oludamola Adebowale, ${publication.topic.join(', ')}, Nigerian cultural historian`}
        canonical={`https://oludamolaadebowale.com/publications/${publication.id}`}
        ogType="book"
        ogImage={publication.coverImage}
        schemaJson={pubSchema}
      />
      <Navigation />
      <div className="pt-20">
        <PublicationDetail publication={publication} relatedPublications={related} />
      </div>
    </div>
  );
}
